// DomainStorage token swap/restore for super-admin "View as user" (spec B4).
// `ds` is the DomainStorage instance (async getItem/setItem/removeItem).
// Every exit path fails CLOSED: if the saved super-admin token cannot be proven to belong to the
// current impersonation session, both tokens are cleared and the user is logged out.
import { postEndImpersonation } from '../api/adminActions'
import {
  IMPERSONATOR_SESSION_KEY,
  IMPERSONATOR_TOKEN_KEY,
  IMPERSONATOR_USER_KEY,
  claimsFromToken,
  exitUrl,
  impersonationInfo,
  landingUrl
} from './impersonation'
import { canStartImpersonation, isRestorable, isTrustedUrlToken } from './impersonationGuard'

const SAVED_KEYS = [IMPERSONATOR_TOKEN_KEY, IMPERSONATOR_USER_KEY, IMPERSONATOR_SESSION_KEY]

const setOrRemove = (ds, key, value) => (value ? ds.setItem(key, value) : ds.removeItem(key))

const isImpersonationToken = (token) => !!impersonationInfo(claimsFromToken(token))

// Drops a saved super-admin token so it can never be restored over another session.
const discardImpersonator = (ds) => Promise.all(SAVED_KEYS.map((key) => ds.removeItem(key)))

// Full logout: no session token, no saved super-admin token, no cached user.
const clearAll = async (ds) => {
  localStorage.removeItem('userData')
  const keys = ['accessToken', 'userData', ...SAVED_KEYS]
  await Promise.allSettled(keys.map((key) => ds.removeItem(key)))
}

const saveImpersonator = async (ds, accessToken, impersonationToken) => {
  await ds.setItem(IMPERSONATOR_TOKEN_KEY, accessToken)
  await setOrRemove(ds, IMPERSONATOR_USER_KEY, await ds.getItem('userData'))
  await ds.setItem(IMPERSONATOR_SESSION_KEY, impersonationInfo(claimsFromToken(impersonationToken)).impersonationId)
}

export const startImpersonationSession = async (ds, impersonationToken) => {
  const accessToken = await ds.getItem('accessToken')
  if (isImpersonationToken(accessToken)) throw new Error('already_impersonating')
  if (!accessToken) throw new Error('no_session')
  if (!canStartImpersonation(accessToken, impersonationToken)) throw new Error('not_allowed')
  try {
    await saveImpersonator(ds, accessToken, impersonationToken)
    await ds.setItem('accessToken', impersonationToken)
    await ds.removeItem('userData')
  } catch (error) {
    await discardImpersonator(ds).catch(() => null) // never leave partial copies behind
    throw error
  }
  localStorage.removeItem('userData')
}

const restoreVerified = async (ds, impersonationClaims) => {
  const savedToken = await ds.getItem(IMPERSONATOR_TOKEN_KEY)
  const savedSessionId = await ds.getItem(IMPERSONATOR_SESSION_KEY)
  if (!isRestorable({ savedToken, savedSessionId, impersonationClaims })) return false
  const userData = await ds.getItem(IMPERSONATOR_USER_KEY)
  await ds.setItem('accessToken', savedToken)
  await setOrRemove(ds, 'userData', userData)
  await discardImpersonator(ds)
  localStorage.removeItem('userData')
  return true
}

const endAndRestore = async (ds, token, endRequest) => {
  const claims = claimsFromToken(token)
  const info = impersonationInfo(claims)
  if (!info) return false // impersonation token already gone: nothing to verify against
  // Ending server-side is best effort: an expired session must still restore.
  await Promise.resolve().then(() => endRequest(info.impersonationId, token)).catch(() => null)
  return restoreVerified(ds, claims)
}

// Returns true only when the verified super-admin session was restored; otherwise the user is
// logged out (or, if a normal session is active, that session is kept and stale copies dropped).
export const exitImpersonation = async (ds, endRequest) => {
  try {
    const token = await ds.getItem('accessToken')
    if (token && !isImpersonationToken(token)) {
      await discardImpersonator(ds)
      return false
    }
    if (await endAndRestore(ds, token, endRequest)) return true
  } catch (error) {
    // fall through to a full logout
  }
  await clearAll(ds)
  return false
}

// Decides what /logout does. A real logout also discards any saved super-admin token,
// so it cannot linger in DomainStorage and be restored later.
export const logoutAction = async (ds) => {
  if (isImpersonationToken(await ds.getItem('accessToken'))) return 'exit_impersonation'
  await discardImpersonator(ds)
  return 'logout'
}

// Handles a ?_token= login handoff. Impersonation tokens are only ever installed by
// beginViewAsUser, so one arriving in the URL is ignored; a real login drops saved copies.
export const acceptUrlToken = async (ds, token) => {
  if (!isTrustedUrlToken(token)) return
  await discardImpersonator(ds)
  await ds.setItem('accessToken', token)
}

const assignLocation = (url) => { window.location = url }

// `token` is the impersonation token from POST /api/impersonations; `company` the target's company.
export const beginViewAsUser = async (ds, { token, company }, navigate = assignLocation) => {
  await startImpersonationSession(ds, token)
  navigate(landingUrl(company))
}

export const leaveViewAsUser = async (ds, navigate = assignLocation, endRequest = postEndImpersonation) => {
  await exitImpersonation(ds, endRequest)
  navigate(exitUrl())
}
